import { useCallback, useMemo, useRef, useState } from 'react';
import standardCohortKeys from 'js/utils/cohort-keys';
import {
  getAvailableBreakouts,
  resolveAssessmentCohorts,
  getDefaultBreakoutSelection,
  NO_BREAKOUT_SOURCE,
} from 'js/utils/assessment-cohorts';
import {
  COMBINED_CHART_PALETTE_INDEX,
  cohortChartPaletteIndex,
} from 'js/utils/color';
import { useSearchParam } from './useSearchParam';

export const BREAKOUT_SEARCH_PARAM = 'breakout';

export function useAssessmentBreakoutSelection(assessment) {
  const [breakoutParam, setBreakoutParam] = useSearchParam(
    BREAKOUT_SEARCH_PARAM
  );

  const availableBreakouts = useMemo(
    () => getAvailableBreakouts(assessment),
    [assessment]
  );

  const selectedBreakoutId = useMemo(() => {
    if (!breakoutParam) {
      return getDefaultBreakoutSelection(assessment)?.id ?? null;
    }
    const match = availableBreakouts.find(
      (b) => String(b.id) === String(breakoutParam)
    );
    if (match) {
      return match.id;
    }
    return (
      availableBreakouts.find((b) => b.id === NO_BREAKOUT_SOURCE)?.id ?? null
    );
  }, [assessment, availableBreakouts, breakoutParam]);

  const { cohorts, participantCohortMap } = useMemo(() => {
    const resolved = resolveAssessmentCohorts(assessment, selectedBreakoutId);
    return {
      cohorts: resolved.cohorts,
      participantCohortMap: resolved.participantCohortsMap,
    };
  }, [assessment, selectedBreakoutId]);

  const [filter, setFilter] = useState({});
  const [pinnedCohort, setPinnedCohort] = useState(null);
  const handlePinCohort = useCallback((cohort) => {
    setPinnedCohort(cohort);
  }, []);

  const handleClearPinnedCohort = useCallback(() => {
    setPinnedCohort(null);
  }, []);

  const cohortPeekTimeout = useRef(null);
  const [peekedCohort, setPeekedCohort] = useState(null);
  const handlePeekCohort = useCallback((cohort) => {
    clearTimeout(cohortPeekTimeout.current);
    cohortPeekTimeout.current = null;
    setPeekedCohort(cohort);
  }, []);

  const handleClearPeekedCohort = useCallback(() => {
    setPeekedCohort(null);
  }, []);

  const handleBreakoutChange = useCallback(
    (breakoutId) => {
      if (breakoutId != null) {
        setBreakoutParam(String(breakoutId));
      }
      setFilter({});
      setPinnedCohort(null);
      setPeekedCohort(null);
    },
    [setBreakoutParam]
  );

  const activeCohort = peekedCohort ?? pinnedCohort;

  const legendFields = useMemo(() => {
    return [
      {
        paletteIndex: COMBINED_CHART_PALETTE_INDEX,
        key: standardCohortKeys.CUMULATIVE,
        name: '(Combined)',
      },
      ...(cohorts ?? []).map((c, i) => ({
        paletteIndex: cohortChartPaletteIndex(i),
        key: c.value ?? standardCohortKeys.NULL,
        name: c.text,
        disabled: Boolean(c.empty),
      })),
    ];
  }, [cohorts]);

  const activeBreakoutName =
    availableBreakouts.length > 1 && selectedBreakoutId !== NO_BREAKOUT_SOURCE
      ? availableBreakouts.find((b) => b.id === selectedBreakoutId)?.name ??
        null
      : null;

  const cohortProps = {
    onPinCohort: handlePinCohort,
    onClearPinnedCohort: handleClearPinnedCohort,
    onPeekCohort: handlePeekCohort,
    onClearPeekedCohort: handleClearPeekedCohort,
    cohorts,
    activeCohort,
    pinnedCohort,
    peekedCohort,
    legendFields,
    participantCohortMap,
    breakoutName: activeBreakoutName,
  };

  return {
    selectedBreakoutId,
    handleBreakoutChange,
    filter,
    setFilter,
    availableBreakouts,
    cohorts,
    participantCohortMap,
    legendFields,
    activeBreakoutName,
    activeCohort,
    pinnedCohort,
    peekedCohort,
    cohortProps,
    handlePinCohort,
    handleClearPinnedCohort,
    handlePeekCohort,
    handleClearPeekedCohort,
  };
}
